import {
	DndContext,
	type DragEndEvent,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useAdminFormEditorPaths } from "../hooks/use-admin-form-editor-paths";
import {
	alphabetizeFieldOptions,
} from "../lib/field-option-order";

interface FieldOption {
	value: string;
	label: string;
}

interface FieldOptionListEditorProps {
	fieldId: string;
	options: FieldOption[];
	/** Drag reorder and one-shot alphabetize. Radio leaves this false. */
	reorderable: boolean;
	onChange: (options: FieldOption[]) => void;
}

function readTextFieldChangeValue(value: string | { target?: { value?: string } }): string {
	if (typeof value === "string") return value;
	return value.target?.value ?? "";
}

function optionRowId(fieldId: string, index: number): string {
	return `${fieldId}-opt-${index}`;
}

export function FieldOptionListEditor({
	fieldId,
	options,
	reorderable,
	onChange,
}: FieldOptionListEditorProps) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
	);

	const updateLabel = (index: number, raw: string) => {
		const next = [...options];
		const current = next[index];
		if (!current) return;
		next[index] = {
			...current,
			label: raw,
			value: raw,
		};
		onChange(next);
	};

	const removeAt = (index: number) => {
		onChange(options.filter((_, optionIndex) => optionIndex !== index));
	};

	const addOption = () => {
		const nextIndex = options.length + 1;
		const text = String(
			t(paths.formEditor.properties.newOptionLabel, { index: nextIndex }),
		);
		onChange([...options, { value: text, label: text }]);
	};

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const from = options.findIndex(
			(_, index) => optionRowId(fieldId, index) === String(active.id),
		);
		const to = options.findIndex(
			(_, index) => optionRowId(fieldId, index) === String(over.id),
		);
		if (from < 0 || to < 0 || from === to) return;
		const next = [...options];
		const [moved] = next.splice(from, 1);
		if (!moved) return;
		next.splice(to, 0, moved);
		onChange(next);
	};

	const rows = options.map((option, index) =>
		reorderable ? (
			<SortableOptionRow
				key={optionRowId(fieldId, index)}
				rowId={optionRowId(fieldId, index)}
				option={option}
				index={index}
				total={options.length}
				canRemove={options.length > 1}
				onLabelChange={(raw) => updateLabel(index, raw)}
				onRemove={() => removeAt(index)}
			/>
		) : (
			<PlainOptionRow
				key={`${fieldId}-opt-${index}`}
				option={option}
				index={index}
				total={options.length}
				canRemove={options.length > 1}
				onLabelChange={(raw) => updateLabel(index, raw)}
				onRemove={() => removeAt(index)}
			/>
		),
	);

	return (
		<fieldset className="min-w-0 border-0 p-0">
			<legend className="mb-2 text-sm font-medium text-text-primary">
				{t(paths.formEditor.properties.options)}
			</legend>
			<div className="flex flex-col gap-2">
				{reorderable ? (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={onDragEnd}
					>
						<SortableContext
							items={options.map((_, index) => optionRowId(fieldId, index))}
							strategy={verticalListSortingStrategy}
						>
							{rows}
						</SortableContext>
					</DndContext>
				) : (
					rows
				)}
				{reorderable ? (
					<Button
						size="sm"
						color="secondary"
						type="button"
						onClick={() => onChange(alphabetizeFieldOptions(options))}
					>
						{t(paths.formEditor.properties.alphabetizeOptions)}
					</Button>
				) : null}
				<Button size="sm" color="secondary" type="button" onClick={addOption}>
					{t(paths.formEditor.properties.addOption)}
				</Button>
			</div>
		</fieldset>
	);
}

function PlainOptionRow({
	option,
	index,
	total,
	canRemove,
	onLabelChange,
	onRemove,
}: {
	option: FieldOption;
	index: number;
	total: number;
	canRemove: boolean;
	onLabelChange: (value: string) => void;
	onRemove: () => void;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	return (
		<div className="flex gap-2">
			<Input
				aria-label={t(paths.formEditor.properties.optionLabelField, {
					index: index + 1,
					total,
				})}
				className="min-w-0 flex-1"
				value={option.label}
				onChange={(value) => onLabelChange(readTextFieldChangeValue(value))}
			/>
			<Button
				size="sm"
				color="tertiary"
				type="button"
				isDisabled={!canRemove}
				aria-label={t(paths.formEditor.properties.removeOptionNamed, {
					label: option.label,
				})}
				onClick={onRemove}
			>
				{t(paths.formEditor.properties.removeOption)}
			</Button>
		</div>
	);
}

function SortableOptionRow({
	rowId,
	option,
	index,
	total,
	canRemove,
	onLabelChange,
	onRemove,
}: {
	rowId: string;
	option: FieldOption;
	index: number;
	total: number;
	canRemove: boolean;
	onLabelChange: (value: string) => void;
	onRemove: () => void;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const sortable = useSortable({ id: rowId });
	const style = {
		transform: CSS.Transform.toString(sortable.transform),
		transition: sortable.transition,
	};

	return (
		<div ref={sortable.setNodeRef} style={style} className="flex gap-2">
			<button
				type="button"
				className="inline-flex shrink-0 items-center text-fg-quaternary"
				aria-label={t(paths.formEditor.properties.dragOption, {
					label: option.label,
				})}
				{...sortable.attributes}
				{...sortable.listeners}
			>
				<GripVertical className="size-4" aria-hidden />
			</button>
			<Input
				aria-label={t(paths.formEditor.properties.optionLabelField, {
					index: index + 1,
					total,
				})}
				className="min-w-0 flex-1"
				value={option.label}
				onChange={(value) => onLabelChange(readTextFieldChangeValue(value))}
			/>
			<Button
				size="sm"
				color="tertiary"
				type="button"
				isDisabled={!canRemove}
				aria-label={t(paths.formEditor.properties.removeOptionNamed, {
					label: option.label,
				})}
				onClick={onRemove}
			>
				{t(paths.formEditor.properties.removeOption)}
			</Button>
		</div>
	);
}
